import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

// Animated number that counts up when scrolled into view.
// Preserves any non-numeric prefix/suffix, e.g. "+30K", "$1T", "1,883", "100+".
export const CountUp = ({ value, duration = 1.6, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const match = String(value).match(/^([^\d]*)([\d.,]+)(.*)$/);
  const numStr = match ? match[2] : "";
  const hasComma = numStr.includes(",");
  const decimals = numStr.includes(".") ? (numStr.split(".")[1]?.length || 0) : 0;
  const target = match ? parseFloat(numStr.replace(/,/g, "")) || 0 : 0;
  const [display, setDisplay] = useState(decimals ? (0).toFixed(decimals) : "0");

  useEffect(() => {
    if (!inView || !match) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = target * eased;
      let formatted = decimals ? current.toFixed(decimals) : Math.round(current).toString();
      if (hasComma) formatted = Number(formatted).toLocaleString("en-US");
      setDisplay(formatted);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!match) return <span ref={ref} className={className}>{value}</span>;
  return (
    <span ref={ref} className={className}>
      {match[1]}{display}{match[3]}
    </span>
  );
};

export const SectionLabel = ({ children, color = "#BAF173" }) => (
  <div className="flex items-center gap-3 mb-6" data-testid="section-label">
    <span className="h-px w-10" style={{ backgroundColor: color }} />
    <span
      className="font-display text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase"
      style={{ color }}
    >
      {children}
    </span>
  </div>
);
